import styled from "styled-components";
import {
  h1Styles,
  h2Styles,
  minLarge,
  minMedium,
} from "@songtradr-one/components-frontend";
import { AppModuleKey } from "@songtradr-one/shared-common";
import { staticText } from "../../statics/static-text";
import { CreativeServicesAnimation } from "./lottie-animations/creative-services";
import { CustomerSupportAnimation } from "./lottie-animations/customer-support";
import { HelpCenterAnimation } from "./lottie-animations/help-center";
import { LabsAnimation } from "./lottie-animations/labs";
import { LicensesAnimation } from "./lottie-animations/licenses";
import { MusicAnimation } from "./lottie-animations/music";
import { PayoutsAnimation } from "./lottie-animations/payouts";
import { ResourcesAnimation } from "./lottie-animations/resources";
import { SocialClearancesAnimation } from "./lottie-animations/social-clearances";
import { StatementsAnimation } from "./lottie-animations/statements";

// TEXT
export const text = staticText.controlCenter;

// STYLES
export const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3rem;
  width: 100%;
  padding-top: 2rem;
  padding-bottom: 2rem;
  background: transparent;
`;

export const Title = styled.h1`
  position: relative;
  width: 90vw;
  text-align: center;
  ${h2Styles};
  padding: 0 1.5rem;

  @media ${minMedium} {
    width: 70vw;
  }

  @media ${minLarge} {
    ${h1Styles};
  }
`;

export const TilesContainer = styled.div<TilesContainerProps>`
  display: grid;
  grid-template-columns: repeat(
    ${({ $maxSmallColumns }) => $maxSmallColumns},
    auto
  );
  gap: 0.75rem;
  justify-content: center;
  width: 100%;

  @media ${minMedium} {
    grid-template-columns: repeat(
      ${({ $maxMediumColumns }) => $maxMediumColumns},
      auto
    );
    gap: 1rem;
  }

  @media ${minLarge} {
    grid-template-columns: repeat(
      ${({ $minLargeColumns }) => $minLargeColumns},
      auto
    );
    gap: 1.5rem;
  }
`;

// DATA

// TYPES
export type Props = {
  arrowKeyNavigation?: ArrowKeyNavigation; // Optional prop for enabling arrow key navigation, otherwise the tiles in the control center will be focusable via tab key
};

type ArrowKeyNavigation = {
  getItemProps: (
    userProps?: React.HTMLProps<HTMLElement>,
  ) => Record<string, unknown>;
  activeIndex: number | null;
  listRef: React.MutableRefObject<(HTMLElement | null)[]>;
};

type TilesContainerProps = {
  $maxSmallColumns: number;
  $maxMediumColumns: number;
  $minLargeColumns: number;
};

// CONSTANTS
export const moduleImageByKey: Partial<Record<AppModuleKey, unknown>> = {
  [AppModuleKey.MUSIC]: MusicAnimation,
  [AppModuleKey.RESOURCES]: ResourcesAnimation,
  [AppModuleKey.LICENSES]: LicensesAnimation,
  [AppModuleKey.STATEMENTS]: StatementsAnimation,
  [AppModuleKey.PAYOUTS]: PayoutsAnimation,
  [AppModuleKey.SOCIAL_CLEARANCES]: SocialClearancesAnimation,
  [AppModuleKey.CREATIVE_SERVICES]: CreativeServicesAnimation,
  [AppModuleKey.CUSTOMER_SUPPORT]: CustomerSupportAnimation,
  [AppModuleKey.HELP_CENTER]: HelpCenterAnimation,
  [AppModuleKey.LABS]: LabsAnimation, // Placeholder image since there isn't a specific animation for this module
  [AppModuleKey.DISTRIBUTION]: MusicAnimation, // Reuses the music animation until distribution gets its own
};

// FUNCTIONS
// Logic for mobile screens (max 2 columns)
export const getMaxSmallColumnCount = (count: number): number => {
  if (count === 1) return 1;
  return 2;
};

// Logic for tablet screens (max 3 columns, with orphan prevention)
export const getMinMediumColumnCount = (count: number): number => {
  if (count <= 3) return count;
  if (count % 3 === 1) return 2; // 2x2 is better than 3x1
  return 3;
};

// Logic for desktop screens (max 4 columns, with orphan prevention)
export const getMinLargeColumnCount = (count: number): number => {
  if (count <= 4) return count;
  if (count === 5 || count === 6) return 3;
  if (count === 9) return 3;
  return 4;
};

// ASSETS
